import { logOperationalEvent } from 'saddlebag-logger';

import type {
  SkyscannerAdvertsAdsretrievalserviceV1alpha1ProductsGraphicPromo as GraphicPromoProduct,
  SkyscannerAdvertsAdsretrievalserviceV1alpha1ProductsHomepageHero as HomepageHeroProduct,
  SkyscannerAdvertsAdsretrievalserviceV1alpha1ProductsHomepageVideo as HomepageVideoProduct,
  SkyscannerAdvertsAdsretrievalserviceV1alpha1Response as AdsCapabilityResponse,
} from '@skyscanner-internal/ads-retrieval-service-web-http-client';
import type { HomepageAdsCapabilitySliceUpdates } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  AnalyticsProperties,
  Tracking,
} from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/adverts';
import type {
  GraphicPromo,
  GraphicPromoCreative,
} from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/graphicPromo';
import type {
  HomepageHero,
  HomepageHeroCreative,
} from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/homepageHero';
import type {
  HomepageVideo,
  HomepageVideoCreative,
} from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/homepageVideo';

type Advert = AdsCapabilityResponse.Advert;
type GetAdsResponse = AdsCapabilityResponse.GetAdsResponse;
type TrackingPixels = AdsCapabilityResponse.TrackingPixels;
type AdsCapabilityGraphicPromo = GraphicPromoProduct.GraphicPromo;
type AdsCapabilityHomepageHero = HomepageHeroProduct.HomepageHero;
type AdsCapabilityHomepageVideo = HomepageVideoProduct.HomepageVideo;

type Format = 'fmt.graphicPromo' | 'fmt.homepageHero' | 'fmt.homepageVideo';
type TrackingFields<IdFormat extends Format> = {
  idCreative: string;
  pixels: Tracking['pixels'];
  analyticsProperties: AnalyticsProperties & { idFormat: IdFormat };
  performanceEventsPayloads?: Tracking['performanceEventsPayloads'];
};

const getCtaUrl = (callToAction?: { type?: string; uri?: string }) =>
  callToAction?.type === 'CALL_TO_ACTION_TYPE_WEBPAGE' ? (callToAction.uri ?? '') : '';

const getTextAlign = (
  horizontalAlignment?: AdsCapabilityGraphicPromo['horizontalAlignment'],
): string => {
  switch (horizontalAlignment) {
    case 'GRAPHIC_PROMO_HORIZONTAL_ALIGNMENT_LEFT':
      return 'left';
    case 'GRAPHIC_PROMO_HORIZONTAL_ALIGNMENT_RIGHT':
      return 'right';
    case 'GRAPHIC_PROMO_HORIZONTAL_ALIGNMENT_CENTER':
      return 'center';
    default:
      return 'center';
  }
};

const getInvertVertically = (
  verticalAlignment?: AdsCapabilityGraphicPromo['verticalAlignment'],
): boolean => verticalAlignment === 'GRAPHIC_PROMO_VERTICAL_ALIGNMENT_TOP';

const getPixels = (pixels?: TrackingPixels): Tracking['pixels'] => ({
  ...pixels,
  impression: pixels?.impression ?? [],
});

const createTracking = <IdFormat extends Format>(
  advert: Advert,
  idFormat: IdFormat,
): TrackingFields<IdFormat> | null => {
  const trackingContext = advert.trackingContext ?? {};
  const { idCreative } = trackingContext;

  if (!idCreative) {
    return null;
  }

  return {
    analyticsProperties: {
      idCreative,
      idFormat,
      idPlacement: trackingContext.idPlacement ?? '',
      idTracking: trackingContext.idTracking ?? '',
    },
    idCreative,
    pixels: getPixels(advert.trackingPixels),
    performanceEventsPayloads: advert.performanceEventsPayloads,
  };
};

const transformHomepageHero = (
  advert: Advert,
  homepageHero: AdsCapabilityHomepageHero,
): HomepageHero | null => {
  const tracking = createTracking(advert, 'fmt.homepageHero');

  if (!tracking) {
    return null;
  }

  const ctaUrl = getCtaUrl(homepageHero.callToAction);

  if (!ctaUrl) {
    logInvalidCta('fmt.homepageHero', tracking.idCreative);
    return null;
  }

  const creative: HomepageHeroCreative = {
    headline: homepageHero.headline ?? '',
    backgroundImageDesktop: homepageHero.backgroundImageDesktop ?? '',
    backgroundImageMobile: homepageHero.backgroundImageMobile ?? '',
    backgroundImageAltText: homepageHero.backgroundImageAltText ?? '',
    buttonText: homepageHero.callToAction?.buttonText ?? '',
    ctaUrl,
    pioPartnerName: homepageHero.partnerBillingInfo?.pioPartnerName ?? '',
    pioAgencyName: homepageHero.partnerBillingInfo?.pioAgencyName,
  };

  if (homepageHero.backgroundColour) {
    creative.backgroundColour = homepageHero.backgroundColour;
  }
  if (homepageHero.fontColour) {
    creative.fontColour = homepageHero.fontColour;
  }
  if (homepageHero.imageTransparent !== undefined) {
    creative.isImageTransparent = homepageHero.imageTransparent;
  }
  if (homepageHero.callToAction?.backgroundColour) {
    creative.ctaBackgroundColour = homepageHero.callToAction.backgroundColour;
  }
  if (homepageHero.callToAction?.textColour) {
    creative.ctaFontColour = homepageHero.callToAction.textColour;
  }
  if (homepageHero.partnerName || homepageHero.partnerLogo) {
    creative.partner = {
      name: homepageHero.partnerName ?? '',
      logo: homepageHero.partnerLogo ?? '',
    };
  }

  return {
    analyticsProperties: tracking.analyticsProperties,
    creative,
    pixels: tracking.pixels,
    performanceEventsPayloads: tracking.performanceEventsPayloads,
  };
};

const transformHomepageVideo = (
  advert: Advert,
  homepageVideo: AdsCapabilityHomepageVideo,
): HomepageVideo | null => {
  const tracking = createTracking(advert, 'fmt.homepageVideo');

  if (!tracking) {
    return null;
  }

  const ctaUrl = getCtaUrl(homepageVideo.callToAction);

  if (!ctaUrl) {
    logInvalidCta('fmt.homepageVideo', tracking.idCreative);
    return null;
  }

  const creative: HomepageVideoCreative = {
    headline: homepageVideo.headline ?? '',
    videoUrl: homepageVideo.videoUri ?? '',
    videoPosterImage: homepageVideo.videoPosterImage ?? '',
    ctaUrl,
    ctaCopy: homepageVideo.callToAction?.buttonText ?? '',
    partnerName: homepageVideo.partnerName ?? '',
    partnerLogo: homepageVideo.partnerLogo ?? '',
    pioPartnerName: homepageVideo.partnerBillingInfo?.pioPartnerName ?? '',
    pioAgencyName: homepageVideo.partnerBillingInfo?.pioAgencyName,
  };

  if (homepageVideo.message) {
    creative.message = homepageVideo.message;
  }
  if (homepageVideo.videoLocation) {
    creative.videoLocation = homepageVideo.videoLocation;
  }
  if (homepageVideo.videoCountry) {
    creative.videoCountry = homepageVideo.videoCountry;
  }

  return {
    analyticsProperties: tracking.analyticsProperties,
    creative,
    pixels: tracking.pixels,
    performanceEventsPayloads: tracking.performanceEventsPayloads,
  };
};

const transformGraphicPromo = (
  advert: Advert,
  graphicPromo: AdsCapabilityGraphicPromo,
): GraphicPromo | null => {
  const tracking = createTracking(advert, 'fmt.graphicPromo');

  if (!tracking) {
    return null;
  }

  const callToAction =
    graphicPromo.callToActions?.find((cta) => cta.type === 'CALL_TO_ACTION_TYPE_WEBPAGE') ??
    graphicPromo.callToActions?.[0];
  const ctaUrl = getCtaUrl(callToAction);

  if (!ctaUrl) {
    logInvalidCta('fmt.graphicPromo', tracking.idCreative);
    return null;
  }

  const creative: GraphicPromoCreative = {
    headline: graphicPromo.headline ?? '',
    buttonText: callToAction?.buttonText ?? '',
    ctaUrl,
    textAlign: getTextAlign(graphicPromo.horizontalAlignment),
    invertVertically: getInvertVertically(graphicPromo.verticalAlignment),
  };

  if (graphicPromo.backgroundColour) {
    creative.backgroundColour = graphicPromo.backgroundColour;
  }
  if (graphicPromo.kicker) {
    creative.tagline = graphicPromo.kicker;
  }
  if (graphicPromo.subHeadline) {
    creative.subheading = graphicPromo.subHeadline;
  }
  if (graphicPromo.backgroundImageDesktop) {
    creative.backgroundImageDesktop = graphicPromo.backgroundImageDesktop;
  }
  if (graphicPromo.backgroundImageMobile) {
    creative.backgroundImageMobile = graphicPromo.backgroundImageMobile;
  }
  if (graphicPromo.fontColour) {
    creative.textColor = graphicPromo.fontColour;
  }
  if (graphicPromo.sponsorshipMessage) {
    creative.sponsor = {
      logo: graphicPromo.sponsorshipMessage.sponsorshipToutLogo ?? '',
      altText: graphicPromo.sponsorshipMessage.text ?? '',
    };
  }

  return {
    analyticsProperties: tracking.analyticsProperties,
    creative,
    pixels: tracking.pixels,
    performanceEventsPayloads: tracking.performanceEventsPayloads,
  };
};

const logUnknownCreative = (slot: string) => {
  logOperationalEvent({
    eventName: 'homepage_ads_capability_unknown_creative',
    errorType: 'unknown_creative',
    slot,
  });
};

const logInvalidCta = (idFormat: Format, idCreative: string) => {
  logOperationalEvent({
    eventName: 'homepage_ads_capability_invalid_cta',
    errorType: 'invalid_cta',
    idFormat,
    idCreative,
  });
};

export const transformHomepageAdsCapabilityResponse = (
  response: GetAdsResponse,
): HomepageAdsCapabilitySliceUpdates => {
  const updates: HomepageAdsCapabilitySliceUpdates = {
    homepageHeroes: {},
    homepageVideos: {},
    graphicPromos: {},
  };

  Object.entries(response.slotAdverts ?? {}).forEach(([slot, adverts]) => {
    (adverts.items ?? []).forEach((advert) => {
      const { creative } = advert;

      if (creative?.homepageHero) {
        const entry = transformHomepageHero(advert, creative.homepageHero);
        const idCreative = entry?.analyticsProperties.idCreative;

        if (entry && idCreative) {
          updates.homepageHeroes[idCreative] = entry;
        }

        return;
      }

      if (creative?.homepageVideo) {
        const entry = transformHomepageVideo(advert, creative.homepageVideo);
        const idCreative = entry?.analyticsProperties.idCreative;

        if (entry && idCreative) {
          updates.homepageVideos[idCreative] = entry;
        }

        return;
      }

      if (creative?.graphicPromo) {
        const entry = transformGraphicPromo(advert, creative.graphicPromo);
        const idCreative = entry?.analyticsProperties.idCreative;

        if (entry && idCreative) {
          updates.graphicPromos[idCreative] = entry;
        }

        return;
      }

      logUnknownCreative(slot);
    });
  });

  return updates;
};
